import { clsx } from 'clsx'

/**
 * Agent identity uses BOTH colour AND glyph AND shape so even on a glance
 * across 14 lanes the type is unambiguous (foldable phones squish UI).
 *
 * Claude = Anthropic-orange circle, "C" (the Claude app-icon terracotta)
 * Codex  = blue square, "X" (OpenAI/Codex product blue)
 * Gemini = amber diamond, "G"
 * Cursor = violet squircle, "R"
 * Grok   = black circle, "K"
 * Mixed  = split provider badge, "M"
 */
type BadgeAgent = 'claude' | 'codex' | 'codex-secondary' | 'gemini' | 'cursor' | 'grok' | 'mixed'

const LANE_FAMILIES: Record<string, { glyph: string; label: string }> = {
  claude: { glyph: 'C', label: 'Claude' },
  codex: { glyph: 'X', label: 'Codex' },
  gemini: { glyph: 'G', label: 'Gemini' },
  cursor: { glyph: 'R', label: 'Cursor' },
  grok: { glyph: 'K', label: 'Grok Build' },
}

/** A lane's name ("X6", "C17") belongs to the lane, not to the model that is
 *  answering in it right now. Switching a chat's model (Mark, 27 Oct 2026:
 *  X6 and X19 moved onto Claude when Codex usage ran out) must not rename the
 *  lane to "C6", where it collides with the real Claude lane 6. The glyph and
 *  number come from lane_id (pwa-<family>-[...-]lane-<n>); the badge colour and
 *  shape still show the agent currently running it. */
export function laneIdentity(laneId: string | null | undefined): { glyph: string; label: string; number: number } | null {
  const match = laneId?.match(/^pwa-([a-z]+)-(?:[a-z]+-)*lane-(\d+)$/)
  const family = match ? LANE_FAMILIES[match[1]] : undefined
  if (!match || !family) return null
  return { glyph: family.glyph, label: family.label, number: parseInt(match[2], 10) }
}

export default function AgentBadge({
  agent,
  size = 24,
  laneId,
  sessionRef,
}: {
  agent: BadgeAgent
  size?: number
  /** The chat's lane_id; omit for topics, groups and unnumbered badges. */
  laneId?: string | null
  /** The Code conversation's current reference; independent of provider identity. */
  sessionRef?: number | null
}) {
  const lane = laneIdentity(laneId)
  const visualAgent = agent === 'codex-secondary' ? 'codex' : agent
  const isClaude = visualAgent === 'claude'
  const isCodex = visualAgent === 'codex'
  const isGemini = visualAgent === 'gemini'
  const isCursor = visualAgent === 'cursor'
  const isGrok = visualAgent === 'grok'
  const isMixed = visualAgent === 'mixed'
  const label = isClaude
    ? 'Claude'
    : isGemini
      ? 'Gemini'
      : isCursor
        ? 'Cursor'
        : isGrok
          ? 'Grok Build'
        : isMixed
          ? 'Mixed agents'
          : 'Codex'
  const glyph = isClaude ? 'C' : isCodex ? 'X' : isGemini ? 'G' : isCursor ? 'R' : isGrok ? 'K' : 'M'
  const reference = typeof sessionRef === 'number' && Number.isSafeInteger(sessionRef) && sessionRef > 0
    ? sessionRef
    : null
  const badgeText = lane ? `${lane.glyph}${lane.number}` : reference ? `${glyph}${reference}` : glyph
  const laneTitle = !lane
    ? reference ? `${label} session ${reference}` : label
    : lane.label === label
      ? `${label} lane ${lane.number}`
      : `${lane.label} lane ${lane.number}, running ${label}`
  return (
    <span
      className={clsx(
        'inline-grid place-items-center text-[10px] font-bold text-white shrink-0',
        isClaude && 'rounded-full bg-[#D97757]',
        isCodex && 'rounded-md bg-blue-600',
        isGemini && 'rotate-45 rounded-sm bg-amber-600',
        isCursor && 'rounded-lg bg-violet-600',
        isGrok && 'rounded-full bg-zinc-950 ring-1 ring-zinc-500',
        isMixed && 'rounded-full',
      )}
      style={{
        width: size,
        height: size,
        ...(badgeText.length > 3 ? { fontSize: Math.min(10, (size - 2) / (badgeText.length * 0.65)) } : {}),
        ...(isMixed
          ? {
              background:
                'conic-gradient(from 45deg, #D97757 0 33%, #2563eb 33% 66%, #d97706 66% 100%)',
            }
          : {}),
      }}
      aria-label={laneTitle}
      title={laneTitle}
    >
      <span className={isGemini ? '-rotate-45' : undefined}>
        {badgeText}
      </span>
    </span>
  )
}
